import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useReactiveVar } from '@apollo/client/react';
import {
  Box,
  FormControlLabel,
  FormGroup,
  Typography,
  Tooltip,
  Grid,
  Skeleton,
} from '@mui/material';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import Button from '@brainpool/components/atoms/Button/Button';
import Checkbox from '@brainpool/components/molecules/Checkbox/Checkbox';
import TextField from '@brainpool/components/atoms/TextField/TextField';
import { reactiveVars, OrganizationProps } from '@brainpool/apollo-connect';
import { loggerService } from '@brainpool/logger';
import { Modal } from '@brainpool/components/molecules/Modal/Modal';

import { DocumentSource } from '../../types/Documents';
import { ContextProps } from '../../apollo/context';
import UppyUpload from '../../components/Uppy-V2/Uppy-V2';
import { useCreateCase } from '../../hooks/useCreateCase';
import { PageContainer } from '../../components/PageContainer/PageContainer';
import { goToPage } from '../../utils/commonFunctions';
import { WebsocketService } from '../../services/websocketService';
import FitScoreNotification from '../../components/FitScoreNotification/FitScoreNotification';
import { useCheckUsageLimit } from '../../hooks/useCheckUsageLimit';
import { useGetUser } from '../../hooks/useGetUser';

interface FormData {
  caseName: string;
  description: string;
  additionalResearchDocuments: boolean;
  terms: boolean;
}

export function NewProject() {
  const navigate = useNavigate();

  //hooks
  const { createCase, data } = useCreateCase();
  const {
    checkUsageLimit,
    data: usageLimitData,
    loading: usageLimitLoading,
  } = useCheckUsageLimit();
  const { getUser, data: userData } = useGetUser({ useCache: true });

  // get data from cache
  const { getUser: user } = userData || {};
  const { survey_responses } = user || {};

  //reactive vars
  const token = useReactiveVar<string>(reactiveVars.token);
  const organization = useReactiveVar<OrganizationProps>(reactiveVars.organization);
  const companionEndpoint = useReactiveVar<ContextProps['companionEndpoint']>(
    reactiveVars.companionEndpoint
  ) as string;

  //state
  const userUppyRef = useRef<any>(null);
  const companyUppyRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasAdditionalResearchDocuments, setHasAdditionalResearchDocuments] =
    useState<boolean>(false);
  const [userDocs, setUserDocs] = useState<any[]>([]);
  const [companyDocs, setCompanyDocs] = useState<any[]>([]);
  const [userDocsUploadComplete, setUserDocsUploadComplete] = useState(false);
  const [companyDocsUploadComplete, setCompanyDocsUploadComplete] = useState(false);

  useEffect(() => {
    getUser();
    checkUsageLimit({
      operation: 'createCase',
      pipelineType: 'screener',
    });
  }, []);

  useEffect(() => {
    if (data?.createCase) {
      const wsService = WebsocketService.getInstance();
      wsService.subscribeToCaseUpdates(data.createCase.id);
      const connectionStatus = wsService.getConnectionStatus(data.createCase.id);
      loggerService.log({
        logLevel: 'info',
        message: 'WebSocket connection status',
        context: {
          caseId: data.createCase.id,
          connectionStatus,
        },
      });
      userUppyRef.current?.startCoordinatedUpload(data.createCase.id, organization?.id);
      companyUppyRef.current?.startCoordinatedUpload(data.createCase.id, organization?.id);
    }
  }, [data]);

  useEffect(() => {
    if (companyDocsUploadComplete && (userDocs.length === 0 || userDocsUploadComplete)) {
      handleUploadSuccess();
    }
  }, [userDocsUploadComplete, companyDocsUploadComplete, userDocs.length]);

  //functions
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      caseName: '',
      terms: false,
    },
    mode: 'onChange',
  });

  const handleUserUploadSuccess = () => setUserDocsUploadComplete(true);
  const handleCompanyUploadSuccess = () => setCompanyDocsUploadComplete(true);

  const handleUploadSuccess = () => {
    if (data?.createCase?.name) {
      sessionStorage.setItem(`caseName:${data.createCase.id}`, data.createCase.name);
      goToPage(`/projects`, navigate);
    }
  };

  const onSubmit = async (formData: FormData) => {
    setLoading(true);
    createCase({
      name: formData.caseName,
    });
  };

  const onAdditionalResearchDocumentsChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasAdditionalResearchDocuments(e.target.checked);
  };

  if (usageLimitLoading) {
    return (
      <Skeleton width={'100%'} height={200} sx={{ transform: 'unset' }} aria-label={`loading`} />
    );
  }

  if (!usageLimitData?.isUsageLimitExceeded?.allowed) {
    return <UserLimitReached />;
  }

  return (
    <PageContainer title="New Project" boxed>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FitScoreNotification survey_responses={survey_responses} />

        <Grid container sx={{ flexGrow: 1 }} spacing={4} direction="row">
          <Grid size={{ xs: 12, md: 6 }}>
            <FormGroup sx={{ mb: 2 }}>
              <Controller
                name="caseName"
                control={control}
                rules={{ required: 'Project name is required' }}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    id="caseName"
                    {...field}
                    placeholder="e.g. Name of the Startup"
                    label={'Project Name'}
                    error={!!error}
                    helperText={error?.message}
                    required
                    outerLabel
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>

        <FormGroup>
          <Typography
            variant="h3"
            component="h2"
            sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 4 }}
          >
            Upload Files
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
          >
            Upload data room documents (pdf documents only). Max 1 pitch deck and 5 supporting
            files. Please note xlsx and csv files are not yet accepted.
          </Typography>

          {companionEndpoint && (
            <Grid container spacing={2} direction="column">
              <Grid>
                <Box sx={{ mb: 2, width: '100%' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <UploadRoundedIcon fontSize="medium" sx={{ mr: 1 }} /> Upload Data Room
                    Documents (e.g. Pitch Deck)
                  </Typography>
                  <UppyUpload
                    pipelineType="screener"
                    ref={userUppyRef}
                    companionEndpoint={companionEndpoint}
                    authToken={token}
                    onFilesChange={setCompanyDocs}
                    onUploadSuccess={handleCompanyUploadSuccess}
                    documentSource={DocumentSource.COMPANY}
                    fileLimit={1}
                  />
                </Box>
              </Grid>
              <Grid>
                <FormGroup sx={{ mb: 1 }}>
                  <Controller
                    name="additionalResearchDocuments"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="additionalResearchDocuments"
                            checked={hasAdditionalResearchDocuments}
                            onChange={onAdditionalResearchDocumentsChecked}
                          />
                        }
                        label={
                          <Typography
                            variant="h6"
                            component="span"
                            fontFamily="Fustat, Arial, sans-serif"
                          >
                            Do you want to add your own research data?
                          </Typography>
                        }
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              {hasAdditionalResearchDocuments && (
                <Grid>
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
                    >
                      <UploadRoundedIcon fontSize="medium" sx={{ mr: 1 }} /> Upload Your Desk
                      Research (e.g. competitive analysis, research papers, internal reports)
                    </Typography>
                    <UppyUpload
                      pipelineType="screener"
                      ref={companyUppyRef}
                      companionEndpoint={companionEndpoint}
                      authToken={token}
                      onFilesChange={setUserDocs}
                      onUploadSuccess={handleUserUploadSuccess}
                      documentSource={DocumentSource.USER}
                      fileLimit={5}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </FormGroup>

        <FormGroup sx={{ mb: 0 }}>
          <Controller
            name="terms"
            control={control}
            rules={{ required: 'You must accept the terms and conditions' }}
            render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="terms"
                      required
                      checked={value}
                      onChange={e => onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="h6"
                      component="span"
                      fontFamily="Fustat, Arial, sans-serif"
                    >
                      I accept the{' '}
                      <NavLink
                        key="terms"
                        to="https://tunedd.ai/terms"
                        target="_blank"
                        style={{ color: '#00A86B' }}
                      >
                        Terms & Conditions
                      </NavLink>
                    </Typography>
                  }
                />
                {error && (
                  <Typography color="error" variant="caption">
                    {error.message}
                  </Typography>
                )}
              </>
            )}
          />
          <FormGroup sx={{ display: 'flex', flexDirection: 'row', marginBottom: 0 }}>
            <Tooltip
              title={
                !isValid
                  ? 'Please fill all required fields and accept the terms and conditions'
                  : companyDocs.length === 0
                    ? 'Please upload at least one Data Room document'
                    : ''
              }
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  sx={{ marginTop: '1rem' }}
                  label={loading ? '' : 'Next'}
                  isLoading={loading}
                  disabled={!isValid || companyDocs.length === 0 || loading}
                />
              </span>
            </Tooltip>
          </FormGroup>
        </FormGroup>
      </Box>
    </PageContainer>
  );
}

export default NewProject;
function UserLimitReached() {
  const navigate = useNavigate();

  return (
    <Modal
      id="user-limit-reached"
      open={true}
      onClose={() => navigate('/projects')}
      children={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
            Project Limit Reached
          </Typography>
          <Typography variant="h5">You've reached your current project limit.</Typography>
          <Typography variant="h5">
            To continue using the service without interruption, please upgrade your plan.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 4 }}
            onClick={() => navigate('/billing')}
          >
            Upgrade Your Plan
          </Button>
        </Box>
      }
    />
  );
}
